/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link, useParams } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import UserSegmentToggle from '../common/UserSegmentToggle';
import { useUserSegment, watchFilterValue } from '../../context/UserSegmentContext';
import { formatUTCDate } from '../../utils/utils';
import ExportButton from '../active-users/ExportButton';
import DateRangeBar, { fmt } from '../active-users/DateRangeBar';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import PauseHistorySummaryCards from '../pause/PauseHistorySummaryCards';
import '../active-users/active-users.css';

const LIMIT = 20;
const EXPORT_CAP = 50000;
const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'medicine_name', label: 'Medicine Name' },
  { key: 'start_date', label: 'Start Date' },
  { key: 'end_date', label: 'End Date' },
  { key: 'medicine_days', label: 'Medicine Days' },
  { key: 'medicine_time', label: 'Medicine Time' },
  { key: 'is_reminder', label: 'Is Reminder' },
  { key: 'remark_data', label: 'Remark' },
  { key: 'is_archive', label: 'Is Archive' },
  { key: 'created_time_stamp', label: 'Created Date Time' },
];

const MedicineMasterDetailsList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userId = id ? atob(id) : '';
  const { segment } = useUserSegment();
  const userSegment = watchFilterValue(segment);

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [rows, setRows] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [truncated, setTruncated] = useState(false);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFrom, setDisplayFrom] = useState(null);
  const [displayTo, setDisplayTo] = useState(null);
  const [preset, setPreset] = useState(null);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // No default date window — the detail page opens all-time; the user can
    // narrow with the date range bar.
    refreshToken();
  }, []);

  useEffect(() => {
    if (token) loadData(1, fromDate, toDate);
  }, [token]);

  const didMountSeg = useRef(false);
  useEffect(() => {
    if (!didMountSeg.current) { didMountSeg.current = true; return; }
    if (!token) return;
    setPage(1);
    loadData(1, fromDate, toDate);
  }, [segment]);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const scope = () => (userId ? `&user_id=${userId}` : '');
  const seg = () => (userSegment ? `&user_segment=${userSegment}` : '');

  const loadData = async (p, f, t) => {
    setLoading(true); setError('');
    const filters = `from_date=${f}&to_date=${t}${scope()}${seg()}`;
    try {
      const [list, sum] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/medicine-master-details?page=${p}&limit=${LIMIT}&${filters}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/medicine-master-summary?${filters}`, auth()),
      ]);
      setRows(list.data.data || []);
      setTotalItem(list.data.totalItem || 0);
      setSummary(sum.data.data || null);
    } catch (err) {
      if (err?.response?.status === 401) setError('auth'); else setError('load');
    } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => { setPage(pageNumber); loadData(pageNumber, fromDate, toDate); };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFrom(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayTo(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    setPreset(key); setFromDate(fromStr); setToDate(toStr);
    setDisplayFrom(fromStr ? new Date(fromStr) : null); setDisplayTo(toStr ? new Date(toStr) : null);
    setPage(1); loadData(1, fromStr, toStr);
  };
  const handleSearch = () => { setPage(1); loadData(1, fromDate, toDate); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFrom(null); setDisplayTo(null);
    setPreset(null); setPage(1);
    loadData(1, '', '');
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/medicine-master-export?from_date=${fromDate}&to_date=${toDate}${scope()}${seg()}`, auth());
    const data = res.data.data || [];
    setTruncated(data.length >= EXPORT_CAP);
    return data.map((r) => ({
      ...r,
      is_reminder: r.is_reminder ? 'Yes' : 'No',
      is_archive: r.is_archive ? 'Yes' : 'No',
    }));
  };

  const hasFilters = !!(fromDate || toDate);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
  const userName = userId ? (rows[0]?.name || '') : '';

  const summaryCards = summary ? [
    { label: 'Total Medicine Master', value: Number(summary.total || 0).toLocaleString() },
    { label: 'Active reminders', value: Number(summary.active_reminder_count || 0).toLocaleString() },
    { label: 'Archived', value: Number(summary.archived_count || 0).toLocaleString() },
  ] : [];

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Medicine › <b>Master details</b></span>
            <span className="au-route">/medicine-master-details</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/user-medicine">← Back</Link></span>
          </div>
          <h1 className="au-title">
            Medicine Master Details
            {userName ? <span className="au-subtitle"> — {userName}</span> : null}
          </h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the medicine master. Please try again." onRetry={() => loadData(page, fromDate, toDate)} />
          ) : (
            <>
              <PauseHistorySummaryCards cards={summaryCards} />

              <DateRangeBar
                preset={preset}
                displayFrom={displayFrom}
                displayTo={displayTo}
                hasRange={hasFilters}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="medicine-range-note">
                The date range filters records by their <b>record date (UTC)</b> — the “Created Date Time” column (<code>DATE(created_time_stamp)</code>).{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all records (no date filter).'}
              </p>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : rows.length === 0 ? (
                <EmptyState title="No records in the selected range/segment" hasFilters onClear={clearFilter}
                  message="No medicine master records match the current range and segment." />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Medicine master records</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="medicine-master-details.csv" />
                  </div>
                  {truncated && (
                    <p className="au-range-note" data-testid="medicine-export-truncated">
                      Export limited to the most recent {EXPORT_CAP.toLocaleString()} records.
                    </p>
                  )}
                  <div className="au-table-wrap">
                    <table className="au-table" data-testid="medicine-master-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th><th>Medicine Name</th>
                          <th>Start Date</th><th>End Date</th><th>Medicine Days</th><th>Medicine Time</th>
                          <th>Is Reminder</th><th>Remark</th><th>Is Archive</th><th>Created Date Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, index) => (
                          <tr key={r.id}>
                            <td data-label="No" className="tnum">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis">{r.name}</td>
                            <td data-label="Email" className="au-ellipsis">{r.email}</td>
                            <td data-label="Medicine Name">{r.medicine_name}</td>
                            <td data-label="Start Date">{formatUTCDate(r.start_date)}</td>
                            <td data-label="End Date">{formatUTCDate(r.end_date)}</td>
                            <td data-label="Medicine Days">{r.medicine_days}</td>
                            <td data-label="Medicine Time">{r.medicine_time}</td>
                            <td data-label="Is Reminder">{r.is_reminder ? 'Yes' : 'No'}</td>
                            <td data-label="Remark">{r.remark_data}</td>
                            <td data-label="Is Archive">{r.is_archive ? 'Yes' : 'No'}</td>
                            <td data-label="Created Date Time">{formatUTCDate(r.created_time_stamp)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicineMasterDetailsList;
